import gqlmin from 'gqlmin'
import { gql } from 'graphql-request'
import { datoQueryHomepageContent } from '../queries/query-homepage'
import { notificationsQuery } from '../queries/query-notifications'
import { handleFetchError } from '@/build-helpers/page'
import { executeQuery } from '@datocms/cda-client'

export const supportedLanguages = ['en', 'fr', 'es', 'pt', 'it', 'ar', 'nl', 'sv', 'de'] // Define your supported locales here

/**
 * Executes a GraphQL request to DatoCMS with exponential backoff and retry logic
 * to handle 429 rate limit errors during the Next.js build.
 *
 * @param {string} query - The GraphQL query string.
 * @param {object} variables - The variables for the GraphQL query.
 * @param {number} maxRetries - The maximum number of retry attempts.
 * @returns {Promise<any>} The response data from the GraphQL request.
 */
export const datoRateLimitedRequest = async (query, variables, _maxRetries = 5) => {
  // --- 1. Variable Normalization (Keep this logic) ---
  const normalizedVariables = { ...variables }
  if (normalizedVariables?.locale === 'default') {
    normalizedVariables.locale = 'en'
  }
  if (normalizedVariables?.slugPrefix === 'default') {
    normalizedVariables.slugPrefix = 'en'
  }
  if (normalizedVariables?.componentsLocale === 'default') {
    normalizedVariables.componentsLocale = 'en'
  }

  // --- 2. Client Setup (Keep this logic) ---
  /* const graphQLClient = new GraphQLClient(process.env.DATO_API_ENDPOINT, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${process.env.DATO_API_KEY}`,
      'X-Environment': `${process.env.DATO_ENVIRONMENT}`
    }
  }) */

  // --- 3. Exponential Backoff Retry Loop ---

  // 🚀 Attempt the request
  return await executeQuery(query, {
    token: process.env.DATO_API_KEY,
    environment: process.env.DATO_ENVIRONMENT,
    variables: normalizedVariables
  })
}

export async function getHomepageContent(locale, slugPrefix) {
  try {
    // Build query and fetch page content
    const pageContent = await datoRateLimitedRequest(gqlmin(datoQueryHomepageContent), {
      locale,
      slugPrefix
    })

    if (pageContent?.marketModel) {
      return pageContent.marketModel.homepage
    }
    return null
  } catch (error) {
    return handleFetchError(error, null, null, locale, slugPrefix)
  }
}

// [locale] / integrations / [...slug] / [integration-category]
export async function getIntegrationCategorySlugs() {
  const query = gql`
    query getIntegrationCategorySlugs {
      allIntegrationCategoryModels {
        id
        slug: _allSlugLocales(fallbackLocales: [en]) {
          locale
          value
        }
        title
        typeField
        bodyTitle
        metaTags
        metaImage {
          title
          alt
          url
        }
        uspBlocks {
          id
          title
          icon
          description {
            value
          }
          anchor
          bulletListColor
        }
        bodyCopy {
          value
        }
      }
    }
  `

  try {
    const response = await datoRateLimitedRequest(query)

    if (response?.allIntegrationCategoryModels < 1) return []
    return response.allIntegrationCategoryModels
  } catch (error) {
    console.error('Error fetching integration category slugs:', error)
    return []
  }
}

export async function getIntegrationCategoryMetaTags(slug, locale) {
  const query = gql`
    query getIntegrationCategorySlugs($slug: String!, $locale: SiteLocale!) {
      allIntegrationCategoryModels(filter: { slug: { eq: $slug } }, locale: $locale) {
        slug: _allSlugLocales(fallbackLocales: [en]) {
          locale
          value
        }
        metaTags
        metaImage {
          title
          alt
          url
        }
      }
    }
  `

  try {
    const response = await datoRateLimitedRequest(query, {
      slug: slug,
      locale: locale
    })

    if (response.allIntegrationCategoryModels.length < 1) return null

    return response.allIntegrationCategoryModels[0]
  } catch (error) {
    console.error('Error fetching integration category meta tags slugs:', error)
    return []
  }
}

export async function getNotifications(locale, marketId) {
  try {
    const response = await fetch('/api/dato-fetch', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        query: notificationsQuery,
        variables: {
          locale: locale,
          marketId: [marketId]
        }
      })
    })

    const res = await response.json()

    if (!res.allNotificationsModels) return []

    return res.allNotificationsModels
  } catch (e) {
    console.error('Error fetching notifications: ', e)
  }
}

// ArticleCategory - Blog Top Request
export function transformCollection(data) {
  // Check if data is an array
  if (Array.isArray(data)) {
    // Initialize an empty array to store transformed items
    const transformedArray = []

    // Iterate through each item in the array
    data.forEach((item) => {
      // Recursively transform each item
      transformedArray.push(transformCollection(item))
    })

    // Return the transformed array
    return transformedArray
  }

  // Check if data is an object
  if (typeof data === 'object' && data !== null) {
    // Initialize an empty object to store transformed object
    let transformedObject = {}

    // Iterate through each key-value pair in the object
    Object.entries(data).forEach(([key, value]) => {
      // If the key is 'sys' and it has an 'id' field, rename it to 'id'
      if (key === 'sys' && value && value.id) {
        transformedObject.id = value.id
      }
      // If the key ends with 'Collection', remove 'Collection' from the key name
      else if (key.endsWith('Collection')) {
        const newKey = key.slice(0, -10) // Remove 'Collection' (10 characters) from the end
        transformedObject[newKey] = transformCollection(value)
      }
      // If the key is 'items', flatten the structure
      else if (key === 'items') {
        // Recursively transform the value of 'items'
        transformedObject = transformCollection(value)
      }
      // Otherwise, keep the key-value pair unchanged
      else {
        transformedObject[key] = transformCollection(value)
      }
    })

    // Return the transformed object
    return transformedObject
  }

  // For any other data type (e.g., string, number), return it as is
  return data
}

export function extractLanguageFields(obj, language) {
  const result = {}

  if (obj.sys && obj.sys.contentType) {
    result.__typename = obj.sys.contentType.sys.id
  }
  // Iterate through fields and extract the specified language version
  for (const key in obj.fields) {
    if (typeof obj.fields === 'object' && Object.prototype.hasOwnProperty.call(obj.fields, key)) {
      let availableLanguage = language
      // Check if the specified language exists, otherwise fallback to 'en'
      if (!obj.fields[key][language]) {
        availableLanguage = 'en'
      }
      result[key] = obj.fields[key][availableLanguage]
      if (
        obj.fields[key][availableLanguage]?.sys?.type === 'Asset' ||
        obj.fields[key][availableLanguage]?.sys?.type === 'Entry'
      ) {
        result[key] = extractLanguageFields(result[key], language)
        result[key].id = obj.fields[key][availableLanguage].sys.id
      }
      if (Array.isArray(result[key])) {
        result[key] = result[key].map((item) => extractLanguageFields(item, language))
      }
    }
  }

  return result
}
